import { createFileRoute, Link as RouterLink, useRouter } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Button, Callout, Flex, Heading, Link, Text } from '@radix-ui/themes'
import { ExclamationTriangleIcon, ExternalLinkIcon, InfoCircledIcon } from '@radix-ui/react-icons'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { measurementTypeSchema } from '@willie/schemas'
import { useTRPC } from '~/utils/trpc'
import { CopyButton } from '~/components/CopyButton'
import { StationOverviewCard } from '~/features/stations/components/StationOverviewCard'
import { StationDataSourcesCard } from '~/features/stations/components/StationDataSourcesCard'
import { StationInstallationsCard } from '~/features/stations/components/StationInstallationsCard'
import { StationHealthCard } from '~/features/stations/components/StationHealthCard'
import { StationLeakDetectionCard } from '~/features/stations/components/StationLeakDetectionCard'
import { RebuildStationIndexButton } from '~/features/stations/components/RebuildStationIndexButton'
import type { StationDetail } from '~/features/stations/types'
import { OrgLink } from '~/features/organizations/components/OrgLink'
import { webappSiteUrl, webappStationUrl } from '~/utils/webapp'

const stationIdSchema = z.string().uuid()

const UNLISTED_TYPE_LABELS: Record<string, string> = { temperature: 'Temperature' }

// The fleet list only covers the types the schema enumerates; the rest are reachable only by URL.
const unlistedTypeLabel = (measurementType: string): string | undefined => {
  if (measurementTypeSchema.safeParse(measurementType).success) return undefined
  return UNLISTED_TYPE_LABELS[measurementType]
}

const StationNotFound = () => (
  <Flex direction="column" gap="2" align="start">
    <Heading as="h1" size="5">
      Station not found
    </Heading>
    <Text size="2" color="gray">
      No station exists with this ID.
    </Text>
    <Link asChild size="2">
      <RouterLink to="/stations">Back to stations</RouterLink>
    </Link>
  </Flex>
)

interface StationHeaderProps {
  station: StationDetail
  shareUrl: string
}

interface WebappButtonProps {
  href: string
  children: ReactNode
}

const WebappButton = ({ href, children }: WebappButtonProps) => (
  <Button asChild size="1" variant="soft" color="gray">
    <a href={href} target="_blank" rel="noopener noreferrer">
      <ExternalLinkIcon />
      {children}
    </a>
  </Button>
)

const StationHeader = ({ station, shareUrl }: StationHeaderProps) => (
  <Flex align="start" justify="between" wrap="wrap" gap="3">
    <Flex direction="column" gap="1">
      <Flex align="center" gap="2">
        <OrgLink orgId={station.org.id} name={station.org.name} size="2" />
        <Text size="2" color="gray" aria-hidden>
          /
        </Text>
        <Link asChild size="2" title="Stations at this site">
          <RouterLink to="/stations" search={{ orgId: station.org.id, siteId: station.site.id }}>
            {station.site.name}
          </RouterLink>
        </Link>
      </Flex>
      <Heading as="h1" size="6">
        {station.name}
      </Heading>
    </Flex>
    <Flex align="center" gap="2" wrap="wrap">
      <WebappButton href={webappSiteUrl(station.site.id)}>Site in webapp</WebappButton>
      <WebappButton href={webappStationUrl(station.site.id, station.id)}>Station in webapp</WebappButton>
      {station.measurement_type === 'water_volume' ? <RebuildStationIndexButton station={station} /> : null}
      <CopyButton value={shareUrl} label="Copy link to this station">
        Copy link
      </CopyButton>
    </Flex>
  </Flex>
)

const StationDetailPage = () => {
  const { stationId } = Route.useParams()
  const trpc = useTRPC()
  const router = useRouter()

  const isWellFormedId = stationIdSchema.safeParse(stationId).success
  const {
    data: station,
    isLoading,
    error
  } = useQuery(trpc.ops.stations.get.queryOptions({ stationId }, { enabled: isWellFormedId }))

  if (!isWellFormedId) return <StationNotFound />
  if (isLoading) return <Text color="gray">Loading…</Text>
  if (error) {
    return (
      <Callout.Root color="red" variant="surface">
        <Callout.Icon>
          <ExclamationTriangleIcon />
        </Callout.Icon>
        <Callout.Text>Could not load this station. {error.message}</Callout.Text>
      </Callout.Root>
    )
  }
  if (!station) return <StationNotFound />

  const shareUrl = new URL(
    router.buildLocation({ to: '/stations/$stationId', params: { stationId } }).href,
    window.location.origin
  ).toString()
  const unlistedType = unlistedTypeLabel(station.measurement_type)

  return (
    <Flex direction="column" gap="4">
      <StationHeader station={station} shareUrl={shareUrl} />

      {unlistedType ? (
        <Callout.Root color="gray" variant="surface" size="1">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>{unlistedType} stations aren&apos;t listed in the fleet view yet.</Callout.Text>
        </Callout.Root>
      ) : null}

      <StationOverviewCard station={station} />
      <StationDataSourcesCard station={station} />
      <StationInstallationsCard station={station} />
      <StationHealthCard station={station} />
      {/* Keyed so the card's expansion resets per station: the route reuses the instance otherwise. */}
      {station.measurement_type === 'water_volume' ? (
        <StationLeakDetectionCard key={station.id} station={station} />
      ) : null}
    </Flex>
  )
}

export const Route = createFileRoute('/stations/$stationId')({
  component: StationDetailPage
})
